/* eslint react-hooks/exhaustive-deps: "warn" */

import { FormEvent, KeyboardEvent, useState } from 'react';

import cn from 'classnames';
import NextLink from 'next/link';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './IndexHero.module.scss';

import { getQuickLinks } from '../../HubListPage.data';
import { topicPath } from '../../paths';
import type { IndexHeroProps } from './IndexHero.types';

export const IndexHero = ({ topics, areas, families, onSearch, lang, className }: IndexHeroProps) => {
  const { t } = useInt();
  const [query, setQuery] = useState('');

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearch(query.trim());
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSearch(query.trim());
    }
  };

  const metrics = [
    { value: topics, label: t('topic explainers') },
    { value: areas, label: t('knowledge areas') },
    { value: families, label: t('topic families') },
  ];

  return (
    <header className={cn(styles['hero'], className)}>
      <div className={styles['inner']}>
        <TitleBlock
          size="page"
          title={
            <span>
              {t('Company data.')}
              <br />
              {t('Clearly explained.')}
            </span>
          }
          description={t(
            'Your guide to business verification, ownership, financial risk and company data. Find a clear answer, understand the evidence and follow the next question.',
          )}
          className={styles['titleBlock']}
        />

        <div className={styles['actions']}>
          <Button type="blue" size="xl" href="#topics" suffixIcon="arrow-forward">
            {t('Explore the topics')}
          </Button>
        </div>

        <form role="search" className={styles['search']} onSubmit={onSubmit}>
          <div className={styles['box']}>
            <textarea
              rows={4}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('Try "What is a UBO?" or "business credit report"')}
              aria-label={t('Search the Knowledge Hub')}
              className={styles['textarea']}
            />

            <button
              type="submit"
              className={cn(styles['submit'], !query.trim() && styles['submitInactive'])}
              aria-label={t('Search the Knowledge Hub')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 19V5M5 12l7-7 7 7"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className={styles['quickLinks']}>
            {getQuickLinks(t).map((link) => (
              <NextLink key={link.slug} href={topicPath(link.slug, lang)} className={styles['quickLink']}>
                {link.label}
              </NextLink>
            ))}
          </div>
        </form>

        <ul className={styles['metrics']}>
          {metrics.map((metric) => (
            <li key={metric.label} className={styles['metric']}>
              <b>{metric.value}</b> {metric.label}
            </li>
          ))}
        </ul>
      </div>
    </header>
  );
};
